import { useState } from 'react';
import { ApiError } from '@/api/client';
import { uploadPersoonsdocument } from '@/api/persoonsdocumenten';
import { compressImage } from '@/utils/imageCompression';

/**
 * Upload one document of one type for a person: the file first, then the date
 * it is valid until. Nothing is read from the file (an ID copy is stored, never parsed).
 * Administratie's own upload is in use at once. With `vervangt`, the document
 * it replaces is archived once this one is stored.
 */
export function PersoonsdocumentUpload({
  partnerId,
  typeId,
  typeName,
  vervangt,
  onUploaded,
  onStored,
  onCancel,
}: {
  partnerId: number;
  typeId: number;
  typeName: string;
  vervangt?: number;
  onUploaded: () => void;
  /** The new document was stored although the form stays open with an error: reload the list. */
  onStored?: () => void;
  onCancel: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [geldigTot, setGeldigTot] = useState('');
  const [opmerking, setOpmerking] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The new document is stored but the old one was not archived: never upload it a second time.
  const [stored, setStored] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!file) return setError('Kies een PDF of foto van het document.');
    if (!geldigTot) return setError('Vul geldig tot in.');
    setBusy(true);
    try {
      const prepared = await compressImage(file);
      await uploadPersoonsdocument(partnerId, { typeId, geldigTot, opmerking: opmerking.trim() || undefined, vervangt, file: prepared });
      onUploaded();
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : 'Document opladen mislukt');
      setBusy(false);
      if (err instanceof ApiError && err.code === 'ARCHIVE_FAILED') {
        setStored(true);
        onStored?.();
      }
    }
  };

  const id = `pd-${typeId}-${vervangt ?? 'nieuw'}`;
  return (
    <form onSubmit={submit} aria-label={`${typeName} ${vervangt ? 'vervangen' : 'uploaden'}`} className="mt-2 border border-gray-200 rounded-lg p-3 space-y-2 text-sm">
      <label className="block text-xs text-td-muted" htmlFor={`${id}-file`}>
        Bestand (PDF of foto)
      </label>
      <input
        id={`${id}-file`}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        className="block w-full text-xs"
      />
      {file && (
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-td-muted">
            Geldig tot
            <input type="date" value={geldigTot} onChange={(e) => setGeldigTot(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" />
          </label>
          <label className="col-span-2 text-xs text-td-muted">
            Opmerking (optioneel)
            <input type="text" maxLength={500} value={opmerking} onChange={(e) => setOpmerking(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" />
          </label>
        </div>
      )}
      {vervangt && <p className="text-xs text-td-light">Het huidige document wordt gearchiveerd.</p>}
      {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy || stored || !file || !geldigTot} className="px-3 py-1.5 text-xs font-semibold text-white bg-td-blue rounded-lg disabled:opacity-60">
          {busy ? 'Bezig met opladen…' : 'Opladen'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className="px-3 py-1.5 text-xs font-semibold text-td-muted border border-gray-300 rounded-lg disabled:opacity-60">
          {stored ? 'Sluiten' : 'Annuleren'}
        </button>
      </div>
    </form>
  );
}
